import { getWindowOrThrow } from "@faire/web--source/common/globals/getWindow";
import { createContextStore } from "@faire/web--source/ui/hooks/ContextStore";
import { useCallback } from "react";

import { getDD_RUM } from "../datadog/rum";
import { getGlobalProperty } from "../globals/getGlobalProperty";

export interface IRouteMetadata {
  productArea: string | undefined;
  pod: string | undefined;
  pillar: string | undefined;
  templatedPath: string | undefined;
}

// checking if window is undefined is for NextJS server not to read global properties
const routeContextStore = createContextStore<IRouteMetadata>({
  productArea:
    // eslint-disable-next-line @faire-web/no-restricted-globals
    typeof window === "undefined"
      ? "PRODUCT_AREA_UNKNOWN"
      : getGlobalProperty<string>("routeProductArea"),
  pod:
    // eslint-disable-next-line @faire-web/no-restricted-globals
    typeof window === "undefined"
      ? "FAIRE_POD_UNKNOWN"
      : getGlobalProperty<string>("routePod"),
  pillar:
    // eslint-disable-next-line @faire-web/no-restricted-globals
    typeof window === "undefined"
      ? "FAIRE_PILLAR_UNKNOWN"
      : getGlobalProperty<string>("routePillar"),
  templatedPath:
    // eslint-disable-next-line @faire-web/no-restricted-globals
    typeof window === "undefined"
      ? "FAIRE_TEMPLATED_PATH_UNKNOWN"
      : getGlobalProperty<string>("templatedPath"),
});

export const useRouteContext = routeContextStore.useStore;
export const RouteContextProvider = routeContextStore.Provider;

export const useSetRouteContext = () => {
  const [, setRouteStore] = routeContextStore.useStore([]);

  return useCallback(
    (routeMetadata: IRouteMetadata) => {
      const normalizedProps = normalizeProps(routeMetadata);
      setRouteStore(normalizedProps);

      // Also update DD RUM context to capture new route metadata
      const existingRumContext = getDD_RUM()?.getRumGlobalContext?.() ?? {};
      const faire: any = existingRumContext.faire ?? {};
      faire.product_area = normalizedProps.productArea;
      faire.pod = normalizedProps.pod;
      faire.pillar = normalizedProps.pillar;
      getDD_RUM()?.addRumGlobalContext?.("faire", faire);

      if (normalizedProps.templatedPath) {
        getDD_RUM()?.addRumGlobalContext?.(
          "templatedPath",
          normalizedProps.templatedPath
        );
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        getWindowOrThrow().templatedPath = normalizedProps.templatedPath;
      }
    },
    [setRouteStore]
  );
};

const normalizeProps = (props: Partial<IRouteMetadata>): IRouteMetadata => {
  return {
    productArea:
      props.productArea === "PRODUCT_AREA_UNKNOWN"
        ? undefined
        : props.productArea,
    pod: props.pod === "FAIRE_POD_UNKNOWN" ? undefined : props.pod,
    pillar: props.pillar === "FAIRE_PILLAR_UNKNOWN" ? undefined : props.pillar,
    templatedPath: props.templatedPath,
  };
};
